import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info } from 'lucide-react';
import CautionBanner from './CautionBanner';
import { Button } from '../../ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../ui/select';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import LoadingRing from '../../ui-components/LoadingRing';
import RoomCard from '../../room-overview/RoomCard';
import { Combobox } from '../../ui/combobox';
import { MachineCompatibilityResponseDTO } from '../../../services/api/mriMachineCompatibilityService';
import { parseCoils } from '../../room-overview/utils';
import { NO_LOCATION } from '../../room-overview/types';

interface CompatibilityResultsProps {
    loading: boolean;
    error: string | null;
    conditionalId: string | null;
    data: MachineCompatibilityResponseDTO | null;
    favoritedMachineIds: Set<string>;
    onFavoriteChange: (machineId: string, isFavorited: boolean) => void;
    onCardClick: (machineId: string) => void;
}

export default function CompatibilityResults({
    loading,
    error,
    conditionalId,
    data,
    favoritedMachineIds,
    onFavoriteChange,
    onCardClick,
}: CompatibilityResultsProps) {
    const { t } = useTranslation();

    const [locationFilter, setLocationFilter] = useState<string>('all');
    const [fieldStrengthFilter, setFieldStrengthFilter] = useState<string>('all');
    const [coilFilter, setCoilFilter] = useState<string>('all');
    const [favoritesOnly, setFavoritesOnly] = useState(false);

    const locationOptions = useMemo(() => {
        if (!data) return [];
        return [
            ...new Set(
                data.results
                    .map((r) => r.machine.location)
                    .filter((location): location is string => !!location)
            ),
        ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }, [data]);

    const hasUnlocatedRooms = useMemo(
        () => !!data && data.results.some((r) => !r.machine.location),
        [data]
    );

    const fieldStrengthOptions = useMemo(() => {
        if (!data) return [];
        return [...new Set(data.results.map((r) => r.machine.FieldStrength))].sort(
            (a, b) => a - b
        );
    }, [data]);

    const coilOptions = useMemo(() => {
        if (!data) return [];
        const stripCoilSuffix = (c: string) => c.replace(/\s*coil$/i, '').trim();
        return data.results
            .flatMap((r) => parseCoils(r.machine.TRCoils))
            .map((c) => stripCoilSuffix(c))
            .filter((coil, i, arr) =>
                arr.findIndex((c) => c.toLowerCase() === coil.toLowerCase()) === i
            )
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }, [data]);

    const filteredResults = useMemo(() => {
        if (!data) return [];
        return data.results
            .filter((r) => {
                if (locationFilter !== 'all') {
                    const matchesLocation =
                        locationFilter === NO_LOCATION
                            ? !r.machine.location
                            : r.machine.location === locationFilter;
                    if (!matchesLocation) return false;
                }
                if (fieldStrengthFilter !== 'all' && r.machine.FieldStrength !== parseFloat(fieldStrengthFilter)) {
                    return false;
                }
                if (coilFilter !== 'all') {
                    const machineCoils = r.machine.TRCoils?.toLowerCase() || '';
                    if (!machineCoils.includes(coilFilter.toLowerCase())) {
                        return false;
                    }
                }
                if (favoritesOnly && !favoritedMachineIds.has(r.machine._id)) {
                    return false;
                }
                return true;
            })
            .sort((a, b) => {
                const aRestricted = a.isRestricted ? 1 : 0;
                const bRestricted = b.isRestricted ? 1 : 0;
                if (aRestricted !== bRestricted) return aRestricted - bRestricted;
                const aFav = favoritedMachineIds.has(a.machine._id) ? 1 : 0;
                const bFav = favoritedMachineIds.has(b.machine._id) ? 1 : 0;
                return bFav - aFav;
            });
    }, [data, locationFilter, fieldStrengthFilter, coilFilter, favoritesOnly, favoritedMachineIds]);

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <LoadingRing size="large" />
            </div>
        );
    }

    if (error) {
        return <p className="text-sm text-destructive text-center py-8">{error}</p>;
    }

    if (!conditionalId) {
        return (
            <p className="text-sm text-subtle-foreground text-center py-8">
                {t('mriCompatibility.noSafetyData')}
            </p>
        );
    }

    if (!data) return null;

    const hasActiveFilters =
        locationFilter !== 'all' || fieldStrengthFilter !== 'all' || coilFilter !== 'all' || favoritesOnly;

    return (
        <div className="space-y-5">
            {data.warnings.length > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-warning/25 bg-warning/10 p-3 text-sm text-warning-foreground">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-warning" />
                    <div>{data.warnings.join('. ')}</div>
                </div>
            )}

            {data.implantCoilWarning && (
                <CautionBanner
                    icon={Info}
                    title={t('mriCompatibility.coilInteraction')}
                    description={t('mriCompatibility.coilInteractionAction')}
                    referenceBlock={{
                        label: t('mriCompatibility.implantConditionLabel'),
                        content: data.implantCoilWarning,
                    }}
                />
            )}

            {data.results.length > 0 && (
                <div className="flex flex-wrap items-center gap-3">
                    {locationOptions.length > 0 && (
                        <Select value={locationFilter} onValueChange={setLocationFilter}>
                            <SelectTrigger className="w-[140px] sm:w-[160px]" aria-label="Filter by location">
                                <SelectValue placeholder={t('roomOverview.machineFilters.allLocations')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('roomOverview.machineFilters.allLocations')}</SelectItem>
                                {locationOptions.map((location) => (
                                    <SelectItem key={location} value={location}>
                                        {location}
                                    </SelectItem>
                                ))}
                                {hasUnlocatedRooms && (
                                    <SelectItem value={NO_LOCATION}>
                                        {t('roomOverview.roomOverview.sections.other')}
                                    </SelectItem>
                                )}
                            </SelectContent>
                        </Select>
                    )}

                    <Select value={fieldStrengthFilter} onValueChange={setFieldStrengthFilter}>
                        <SelectTrigger className="w-[140px] sm:w-[160px]" aria-label="Filter by field strength">
                            <SelectValue placeholder={t('mriCompatibility.filters.fieldStrengthPlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('mriCompatibility.filters.allFieldStrengths')}</SelectItem>
                            {fieldStrengthOptions.map((fs) => (
                                <SelectItem key={fs} value={fs.toString()}>
                                    {t('mriCompatibility.filters.tesla', { value: fs })}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {coilOptions.length > 0 && (
                        <Combobox
                            options={coilOptions}
                            value={coilFilter === 'all' ? '' : coilFilter}
                            onChange={(value) => setCoilFilter(value || 'all')}
                            placeholder={t('mriCompatibility.filters.allCoils')}
                            className="w-[140px] sm:w-[160px]"
                            aria-label="Filter by coil type"
                        />
                    )}

                    {favoritedMachineIds.size > 0 && (
                        <div className="flex items-center gap-2">
                            <Switch
                                id="compat-filter-favorites"
                                checked={favoritesOnly}
                                onCheckedChange={setFavoritesOnly}
                            />
                            <Label htmlFor="compat-filter-favorites" className="text-sm cursor-pointer">
                                {t('roomOverview.machineFilters.favoritesOnly')}
                            </Label>
                        </div>
                    )}

                    {hasActiveFilters && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setLocationFilter('all');
                                setFieldStrengthFilter('all');
                                setCoilFilter('all');
                                setFavoritesOnly(false);
                            }}
                            className="text-subtle-foreground hover:text-foreground"
                        >
                            {t('mriCompatibility.filters.clearFilters')}
                        </Button>
                    )}

                    <span className="ml-auto text-sm text-subtle-foreground">
                        {t('mriCompatibility.filters.resultCount', { filtered: filteredResults.length, total: data.results.length })}
                    </span>
                </div>
            )}

            {data.results.length === 0 ? (
                <p className="text-sm text-subtle-foreground text-center py-8">
                    {t('mriCompatibility.emptyState.noCompatibleRooms')}
                </p>
            ) : filteredResults.length === 0 ? (
                <p className="text-sm text-subtle-foreground text-center py-8">
                    {t('mriCompatibility.emptyState.noFilterMatch')}
                </p>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filteredResults.map((result) => (
                        <RoomCard
                            key={result.machine._id}
                            machine={result.machine}
                            isFavorited={favoritedMachineIds.has(result.machine._id)}
                            onFavoriteChange={onFavoriteChange}
                            restrictionText={result.isRestricted && result.gradientAnalysis ? t('mriCompatibility.restrictionText', {
                                gradient: result.gradientAnalysis.implantMaxGradient,
                            }) : undefined}
                            onClick={onCardClick}
                            showLocation
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
